import React from 'react';
import { PageLayout } from '../components/Layout/PageLayout';
import { FAQAccordion } from '../components/FAQ/FAQAccordion';
import { HelpCircle, Pill, ShieldCheck, AlertCircle, Clock, Users, Phone } from 'lucide-react';
import { SEO } from '../components/SEO';

// FAP categories with detailed answers about prescriptions
const FAP_CATEGORIES = [
  {
    title: "General Prescription Information",
    icon: <Pill className="w-12 h-12 text-[#ff00ff]" />,
    questions: [
      {
        question: "What is a prescription?",
        answer: "A prescription is a written direction from a healthcare provider that authorizes a patient to be provided a medicine or treatment. Prescriptions contain specific instructions for taking medication, including dosage, frequency, and duration of treatment."
      },
      {
        question: "How long is a prescription valid?",
        answer: "In Australia, most standard prescriptions are valid for 12 months from the date of writing. However, certain medications, especially controlled substances, may have shorter validity periods. Always check with your pharmacist if you're unsure about your prescription's validity."
      },
      {
        question: "Can I get a repeat prescription without seeing my doctor?",
        answer: "This depends on your specific circumstances. For ongoing medications with existing repeats, you can usually collect these from your pharmacy without seeing your doctor again. For new prescriptions or when repeats run out, you will typically need to consult with your healthcare provider. Some clinics offer telehealth appointments for prescription renewals."
      }
    ]
  },
  {
    title: "Safety & Compliance",
    icon: <ShieldCheck className="w-12 h-12 text-[#ff00ff]" />,
    questions: [
      {
        question: "How should I store my prescription medications?",
        answer: "Most medications should be stored in a cool, dry place away from direct sunlight. Some may require refrigeration. Always keep medications in their original containers with labels intact, and store them out of reach of children and pets. Follow any specific storage instructions provided by your pharmacist."
      },
      {
        question: "What should I do if I miss a dose?",
        answer: "If you miss a dose, don't automatically take a double dose to make up for it. Check the medication information leaflet or consult your pharmacist for specific advice. For some medications, take the missed dose as soon as you remember unless it's almost time for your next dose. If it's almost time for your next dose, skip the missed dose and take your next scheduled dose at the regular time."
      },
      {
        question: "Can I take prescription medication that was prescribed for someone else?",
        answer: "No. Taking medication prescribed for someone else is dangerous and illegal. Prescriptions are tailored to specific individuals based on their unique health conditions, medical history, weight, age, and other medications they may be taking. What works safely for one person could be harmful or ineffective for another."
      }
    ]
  },
  {
    title: "Timing & Administration",
    icon: <Clock className="w-12 h-12 text-[#ff00ff]" />,
    questions: [
      {
        question: "What does 'take with food' really mean?",
        answer: "When a medication label instructs you to 'take with food,' it means you should take the medication during or immediately after a meal. This is typically recommended to:\n• Reduce stomach irritation\n• Improve absorption of certain medications\n• Minimize side effects like nausea\n\nIf you're unsure about the timing, consult your pharmacist for specific guidance."
      },
      {
        question: "How strict are medication timing requirements?",
        answer: "The importance of precise timing depends on the medication. Some medications (like antibiotics or medications that control blood levels of substances) work best when maintained at steady levels in your body, so timing should be consistent. Others may be less time-sensitive. Your prescription label and pharmacist can provide guidance on how strictly you need to adhere to specific timing."
      },
      {
        question: "Can I crush pills or open capsules if I have trouble swallowing?",
        answer: "Not all medications can be safely crushed or opened. Extended-release, enteric-coated, or specially formulated medications may become ineffective or even harmful if crushed. Always consult your pharmacist before altering any medication form. If you have difficulty swallowing pills, ask your healthcare provider about liquid formulations or other alternatives."
      }
    ]
  },
  {
    title: "Side Effects & Interactions",
    icon: <AlertCircle className="w-12 h-12 text-[#ff00ff]" />,
    questions: [
      {
        question: "What should I do if I experience side effects?",
        answer: "If you experience side effects from your medication:\n• For mild side effects, note them and discuss with your healthcare provider at your next appointment\n• For moderate side effects that are troublesome, contact your healthcare provider for advice\n• For severe or alarming side effects (difficulty breathing, severe rash, extreme dizziness), seek immediate medical attention\n\nNever stop taking prescribed medication without consulting your healthcare provider."
      },
      {
        question: "Can I drink alcohol while taking prescription medications?",
        answer: "Many medications interact negatively with alcohol. Alcohol can increase side effects, reduce effectiveness, or create dangerous health risks when combined with certain prescriptions. Always read medication labels carefully and ask your pharmacist specifically about alcohol interactions. When in doubt, it's safest to avoid alcohol while taking prescription medications."
      },
      {
        question: "How do I know if two medications will interact with each other?",
        answer: "Medication interactions are complex and can occur between prescriptions, over-the-counter drugs, supplements, and even foods. To prevent harmful interactions:\n• Maintain an updated list of all medications and supplements you take\n• Share this complete list with all healthcare providers\n• Use the same pharmacy for all prescriptions when possible\n• Consult your pharmacist or doctor before starting any new medication or supplement"
      }
    ]
  },
  {
    title: "Special Populations",
    icon: <Users className="w-12 h-12 text-[#ff00ff]" />,
    questions: [
      {
        question: "Are prescription guidelines different for elderly patients?",
        answer: "Yes, elderly patients often require special consideration with prescriptions. Age-related changes in metabolism, kidney and liver function, and body composition can affect how medications work. Elderly patients may need adjusted dosages, different monitoring, or alternative medications. Healthcare providers should regularly review all medications taken by elderly patients to ensure safety and effectiveness."
      },
      {
        question: "What should pregnant women know about prescriptions?",
        answer: "Pregnancy requires special caution with medications. Many drugs can cross the placenta and affect fetal development. If you're pregnant or planning to become pregnant, inform all healthcare providers before taking any prescription. Some medications will need to be avoided entirely, while others may require adjusted dosages or careful monitoring. Never discontinue important medications without consulting your healthcare provider."
      }
    ]
  },
  {
    title: "Prescription Assistance",
    icon: <HelpCircle className="w-12 h-12 text-[#ff00ff]" />,
    questions: [
      {
        question: "What if I can't afford my prescription?",
        answer: "If cost is a barrier to filling your prescription, several options may help:\n• Ask about generic alternatives, which are typically less expensive\n• Inquire about patient assistance programs from pharmaceutical companies\n• Check if you qualify for government assistance programs\n• Discuss with your healthcare provider if there are lower-cost alternatives that would be effective for your condition\n• Some pharmacies offer discount programs for uninsured patients"
      },
      {
        question: "Who should I contact with questions about my prescription?",
        answer: "Your pharmacist is an excellent first resource for questions about your medications. They can provide information about dosing, side effects, interactions, and proper administration. For questions about why a medication was prescribed or if it's working properly, contact your prescribing healthcare provider. For urgent concerns about adverse reactions, seek medical attention promptly."
      }
    ]
  }
];

const FAPPage: React.FC = () => {
  return (
    <PageLayout>
      <SEO 
        title="Frequently Asked Prescriptions"
        description="Find answers to common questions about prescriptions, medication safety, side effects, and proper use. Your comprehensive guide to understanding prescriptions."
      />
      <div className="pt-32 pb-16">
        {/* Hero Section */}
        <div className="relative mb-24">
          <div className="absolute inset-0">
            <img
              src="https://images.unsplash.com/photo-1607619056574-7b8d3ee536b2?auto=format&fit=crop&q=80"
              alt="Prescription medication background"
              className="w-full h-[50vh] object-cover opacity-20"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black via-purple-900/40 to-black" />
          </div>
          
          <div className="relative max-w-4xl mx-auto px-4 text-center">
            <h1 className="text-5xl font-bold gradient-text mb-6">
              Frequently Asked Prescriptions
            </h1>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Your comprehensive guide to understanding prescriptions, medication safety, and proper use.
            </p>
          </div>
        </div>

        {/* FAP Categories */}
        <div className="max-w-6xl mx-auto px-4">
          {FAP_CATEGORIES.map((category, index) => (
            <div key={index} className="mb-16">
              <div className="flex items-center gap-4 mb-8">
                {category.icon}
                <h2 className="text-3xl font-bold gradient-text">{category.title}</h2>
              </div>
              
              <div className="space-y-4">
                {category.questions.map((faq, faqIndex) => (
                  <FAQAccordion
                    key={faqIndex}
                    question={faq.question}
                    answer={faq.answer}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Support Section */}
        <div className="max-w-4xl mx-auto px-4 mt-24">
          <div className="glass-panel rounded-3xl p-8 md:p-12 text-center">
            <Phone className="w-16 h-16 text-[#ff00ff] mx-auto mb-6" />
            <h2 className="text-3xl font-bold gradient-text mb-4">Have Medication Questions?</h2>
            <p className="text-xl text-gray-300 mb-8">
              For specific questions about your prescriptions, please consult your healthcare provider or pharmacist. For general inquiries, our support team is here to help.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="mailto:support@raveremedy.com"
                className="px-8 py-3 bg-gradient-to-r from-[#ff00ff] via-[#ff66ff] to-[#00ffff] text-white rounded-xl font-bold hover:opacity-90 transition-all"
              >
                Email Support
              </a>
              <a 
                href="/contact"
                className="px-8 py-3 bg-white/10 text-white rounded-xl font-bold hover:bg-white/20 transition-colors"
              >
                Contact Us
              </a>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default FAPPage;